import { useCallback, useRef, useState } from "react";

/**
 * Observe an element's content width. Returns [refCallback, width], where
 * width is null until the first measurement lands.
 *
 * CONTAINER width, deliberately — not the viewport. The embed mounts inside a
 * Framer page column whose width is unrelated to the window's, so a
 * matchMedia breakpoint would switch layouts at the wrong moment. Container
 * queries are unavailable because the embed ships inline styles only and
 * imports no CSS file, so an @container at-rule has nowhere to live.
 *
 * Fails OPEN: with no ResizeObserver (older browsers, jsdom without the
 * stub) width stays null and callers render their wide-layout default, which
 * is the readable one. A narrow viewport with a broken observer shows a
 * cramped list, not a broken page.
 */
export function useContainerWidth(): [(el: HTMLElement | null) => void, number | null] {
  const [width, setWidth] = useState<number | null>(null);
  const observer = useRef<ResizeObserver | null>(null);

  // No separate unmount-cleanup effect: React always calls a callback ref
  // with `null` when the node unmounts, and the branch below already
  // disconnects + clears `observer.current` on every invocation, including
  // that one. A `useEffect(() => () => observer.current?.disconnect(), [])`
  // here looks like equivalent belt-and-suspenders cleanup but is not: React
  // 18 StrictMode double-invokes *effects* on mount (setup → cleanup →
  // setup again) without re-invoking callback refs (ref
  // detach/reattach-under-StrictMode arrived in React 19, per this repo's
  // pinned react@^18.3.1). The phantom cleanup pass would disconnect the
  // real observer created by the ref callback, and since the ref callback
  // is never called again, no replacement observer is ever created — width
  // stays null forever after the first StrictMode double-invoke.
  const ref = useCallback((el: HTMLElement | null) => {
    observer.current?.disconnect();
    observer.current = null;
    if (!el || typeof window === "undefined" || typeof window.ResizeObserver !== "function") return;
    const ro = new window.ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setWidth(entry.contentRect.width);
    });
    ro.observe(el);
    observer.current = ro;
  }, []);

  return [ref, width];
}
